import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, firstValueFrom, take } from 'rxjs';
import { environment } from '../environments/environment';

export type UserGroups = string[];

const getUserGroups = async (): Promise<UserGroups> => {
  if ((environment as any).msalConfig?.enabled === false) {
    return [];
  }
  const msalService = inject(MsalService, { optional: true });
  const broadcastService = inject(MsalBroadcastService, { optional: true });

  if (!msalService) {
    return [];
  }

  const instance = msalService.instance;
  const getPrimaryAccount = () =>
    instance?.getActiveAccount?.() ?? instance?.getAllAccounts?.()?.[0] ?? null;
  const readGroups = (): UserGroups => {
    const account = getPrimaryAccount();
    if (!account) {
      return [];
    }
    instance?.setActiveAccount?.(account);
    const claims = account.idTokenClaims ?? {};
    const groups =
      (claims['groups'] as UserGroups) ||
      (claims['roles'] as UserGroups) ||
      [];
    return groups;
  };

  const groups = readGroups();
  if (groups.length || !broadcastService) {
    return groups;
  }

  await firstValueFrom(
    broadcastService.inProgress$.pipe(
      filter((status) => status === InteractionStatus.None),
      take(1),
    ),
  );

  return readGroups();
};

// const hasHandlerRole = (groups: UserGroups) =>
//   environment.handlerGroup.some((g) => groups.includes(g));
// const hasManagerRole = (groups: UserGroups) =>
//   environment.managerGroup.some((g) => groups.includes(g));
// export const checkRoute = async (route: 'handler' | 'manager') => {
//   const groups = await getUserGroups();
//   return route === 'handler' ? hasHandlerRole(groups) : hasManagerRole(groups);
// };

const hasUserRole = (groups: UserGroups) =>
  environment.userGroup?.some((g) => groups.includes(g)) ?? false;
const hasSuperUserRole = (groups: UserGroups) =>
  environment.superUserGroup?.some((g) => groups.includes(g)) ?? false;
const hasAnyRole = (groups: UserGroups) =>
  hasUserRole(groups) || hasSuperUserRole(groups);

export const checkAnyRoute: CanMatchFn = async () => {
  const groups = await getUserGroups();
  return hasAnyRole(groups);
};

const resolveLandingTree = (router: Router, groups: UserGroups) => {
  if (hasAnyRole(groups)) {
    return router.createUrlTree(['/', 'agents']);
  }
  return router.createUrlTree(['/', 'login-failed']);
};

export const landingRedirectGuard: CanActivateFn = async (_route, state) => {
  if ((environment as any).msalConfig?.enabled === false) {
    return true;
  }
  const router = inject(Router);
  const normalizedUrl = state.url.split(/[?#]/)[0];
  const isShellEntry = normalizedUrl === '/' || normalizedUrl === '';

  if (!isShellEntry) {
    return true;
  }

  const groups = await getUserGroups();
  return resolveLandingTree(router, groups);
};

export const fallbackRedirectGuard: CanActivateFn = async (_route, _state) => {
  if ((environment as any).msalConfig?.enabled === false) {
    return true;
  }
  const router = inject(Router);
  const groups = await getUserGroups();
  return resolveLandingTree(router, groups);
};

const STUDIO_OWNED_PREFIXES = [
  '/agents',
  '/agent-studio',
  '/knowledge-base',
  '/tests',
  '/logs',
  '/analytics',
  '/settings',
  '/content',
  '/call',
  '/login-failed',
];

export const routeOutsideStudioGuard: CanActivateFn = (_route, state) => {
  const path = state.url.split(/[?#]/)[0];
  const isStudioPath =
    path === '/' ||
    STUDIO_OWNED_PREFIXES.some(
      (p) => path === p || path.startsWith(`${p}/`),
    );
  if (!isStudioPath && typeof window !== 'undefined') {
    window.location.replace(path + window.location.search);
    return false;
  }
  return true;
};
